import { useEffect, useMemo, useState } from "react";

import debounce from "lodash/debounce";

import { useTranslate } from "@similarweb/i18n-provider";
import { Close, Search } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, CircularLoader, EmptyState } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useInfiniteScrollFetch } from "app/components/hooks/useInfiniteScrollFetch";
import { QueryStates } from "app/pages/sales-intelligence/common-components/QueryStates/QueryStates";
import { Z_INDEX } from "app/pages/sales-intelligence/constants/common";
import { usePlaybooksTrackingService } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksTrackingService";
import { SalesloftReconnectPanel } from "app/pages/sales-intelligence/modules/salesloft/components/SalesloftReconnectPanel/SalesloftReconnectPanel";
import { useSalesloftCadences } from "app/pages/sales-intelligence/modules/salesloft/hooks/useSalesloftCadences";
import type { SalesloftCadence } from "app/pages/sales-intelligence/modules/salesloft/types/salesloft.types";

import type { EmailPersonalization } from "../../types/playbooks.types";
import { type CadenceSegment, getCadenceVisibility } from "../../utils/filterCadences";
import { CadenceCard } from "../CadenceCard/CadenceCard";

import { EmailPersonalizationOption } from "./EmailPersonalizationOption";
import { PlaybookCadenceVisibilityToggle } from "./PlaybookCadenceVisibilityToggle";
import { StyledCadenceList } from "./styles";

const SEGMENTS: CadenceSegment[] = ["all", "private", "shared"];
const SHARED_SEGMENT_INDEX = SEGMENTS.indexOf("shared");
const SEARCH_DEBOUNCE_MS = 300;

export type PlaybookCadencePickerProps = {
    isSharedWithAccount: boolean;
    enabled?: boolean;
    onReconnectSuccess?: () => void;
    selectedCadenceId: number | null;
    onSelectCadence: (cadence: SalesloftCadence) => void;
    emailPersonalization: EmailPersonalization;
    onEmailPersonalizationChange: (value: EmailPersonalization) => void;
};

export const PlaybookCadencePicker = ({
    isSharedWithAccount,
    enabled = true,
    onReconnectSuccess,
    selectedCadenceId,
    onSelectCadence,
    emailPersonalization,
    onEmailPersonalizationChange,
}: PlaybookCadencePickerProps) => {
    const { translate } = useTranslate();
    const playbooksTracking = usePlaybooksTrackingService();
    const [segmentIndex, setSegmentIndex] = useState(() => (isSharedWithAccount ? SHARED_SEGMENT_INDEX : 0));
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const debouncedSetSearch = useMemo(
        () => debounce((value: string) => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetSearch(searchTerm);
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [searchTerm, debouncedSetSearch]);

    useEffect(() => {
        if (isSharedWithAccount) {
            setSegmentIndex(SHARED_SEGMENT_INDEX);
        }
    }, [isSharedWithAccount]);

    const segmentValues = useMemo(
        () =>
            SEGMENTS.map((value) => ({
                label: translate(`si.playbooks.cadence.segment.${value}`),
                disabled: isSharedWithAccount && value !== "shared",
            })),
        [isSharedWithAccount, translate],
    );

    const handleSegmentChange = (index: number) => {
        if (isSharedWithAccount) {
            setSegmentIndex(SHARED_SEGMENT_INDEX);
            return;
        }
        setSegmentIndex(index);
    };

    const visibility = getCadenceVisibility(SEGMENTS[segmentIndex]);

    const {
        cadences,
        isLoading,
        isError,
        isReconnectRequired,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        openSalesloftConnect,
    } = useSalesloftCadences({
        enabled,
        name: debouncedSearch,
        visibility,
        onReconnected: onReconnectSuccess,
    });

    const listRef = useInfiniteScrollFetch<HTMLDivElement>({
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    });

    const handleSelectCadence = (cadence: SalesloftCadence) => {
        playbooksTracking.trackSelectCadence();
        onSelectCadence(cadence);
    };

    if (isReconnectRequired) {
        return (
            <Stack
                alignItems="center"
                justifyContent="center"
                flex="1"
                gap="12px"
            >
                <SalesloftReconnectPanel
                    onConnect={() => {
                        playbooksTracking.trackSalesloftReconnect();
                        openSalesloftConnect();
                    }}
                    dataAutomation="playbook-cadence-picker-salesloft-reconnect"
                />
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["300"]}
                    textAlign="center"
                    data-automation="playbook-cadence-picker-salesloft-reconnect-description"
                >
                    {translate("si.playbooks.wizard.cadence.salesloft-reconnect.description")}
                </Text>
            </Stack>
        );
    }

    return (
        <Stack
            gap="24px"
            data-automation="playbook-cadence-picker"
            minHeight="0"
            flex="1"
        >
            <Stack
                flex="1"
                minHeight="0"
                gap="16px"
                padding="16px 4px 16px 16px"
                borderRadius="6px"
                border={`1px solid ${colorsPalettes.midnight["50"]}`}
            >
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    gap="16px"
                    margin="0 12px 0 0"
                >
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon["500"]}
                        data-automation="playbook-cadence-picker-connect-title"
                    >
                        {translate("si.playbooks.cadence.connect.title")}
                    </Text>
                    <Flex gap="16px">
                        <PlaybookCadenceVisibilityToggle
                            isSharedWithAccount={isSharedWithAccount}
                            segmentValues={segmentValues}
                            segmentIndex={segmentIndex}
                            onSegmentChange={handleSegmentChange}
                            tooltipContent={translate("si.playbooks.cadence.segment.shared_only.tooltip")}
                        />
                        <TextField
                            value={searchTerm}
                            placeholder={translate("si.playbooks.wizard.cadence.search.placeholder")}
                            startAddon={<Search size="sm" />}
                            endAddon={
                                searchTerm.length > 0 ? (
                                    <Close
                                        size="sm"
                                        onClick={() => setSearchTerm("")}
                                        cursor="pointer"
                                    />
                                ) : null
                            }
                            type="text"
                            onChange={setSearchTerm}
                            data-automation="playbook-cadence-picker-search"
                        />
                    </Flex>
                </Flex>

                <QueryStates
                    isLoading={isLoading}
                    isError={isError}
                    onRetry={() => {
                        playbooksTracking.trackCadenceRetry();
                        void refetch();
                    }}
                    errorDataAutomation="playbook-cadences-picker-error"
                    errorRetryDataAutomation="playbook-cadences-picker-error-retry"
                />

                {!isLoading && !isError ? (
                    cadences.length > 0 ? (
                        <Flex
                            position="relative"
                            flex="1"
                            minHeight="0"
                        >
                            <StyledCadenceList
                                ref={listRef}
                                data-automation="playbook-cadence-picker-list"
                            >
                                {cadences.map((cadence) => (
                                    <CadenceCard
                                        key={cadence.id}
                                        cadence={cadence}
                                        isSelected={cadence.id === selectedCadenceId}
                                        onSelect={handleSelectCadence}
                                    />
                                ))}
                            </StyledCadenceList>
                            {isFetchingNextPage ? (
                                <Flex
                                    position="absolute"
                                    right="12px"
                                    bottom="0"
                                    left="0"
                                    justifyContent="center"
                                    paddingY="16px"
                                    background={`linear-gradient(180deg, transparent 0%, ${colorsPalettes.carbon["0"]} 40%)`}
                                    zIndex={Z_INDEX.default}
                                    data-automation="playbook-cadence-picker-loading-more"
                                >
                                    <CircularLoader isLarge />
                                </Flex>
                            ) : null}
                        </Flex>
                    ) : (
                        <Flex
                            justifyContent="center"
                            padding="24px 0"
                            data-automation="playbook-cadence-picker-empty"
                        >
                            <EmptyState title={translate("si.playbooks.wizard.cadence.empty.title")} />
                        </Flex>
                    )
                ) : null}
            </Stack>

            <Stack gap="12px">
                <Stack gap="4px">
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon["500"]}
                        data-automation="playbook-cadence-picker-personalization-title"
                    >
                        {translate("si.playbooks.wizard.cadence.personalization.title")}
                    </Text>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon["300"]}
                        data-automation="playbook-cadence-picker-personalization-subtitle"
                    >
                        {translate("si.playbooks.wizard.cadence.personalization.subtitle")}
                    </Text>
                </Stack>

                <Flex gap="16px">
                    <EmailPersonalizationOption
                        isSelected={emailPersonalization === "as-is"}
                        title={translate("si.playbooks.wizard.cadence.personalization.as_is.title")}
                        subtitle={translate("si.playbooks.wizard.cadence.personalization.as_is.subtitle")}
                        onSelect={() => onEmailPersonalizationChange("as-is")}
                        dataAutomation="playbook-cadence-picker-personalization-as-is"
                    />
                    <EmailPersonalizationOption
                        isSelected={false}
                        isDisabled
                        title={translate("si.playbooks.wizard.cadence.personalization.ai_outreach.title")}
                        subtitle={translate("si.playbooks.wizard.cadence.personalization.ai_outreach.subtitle")}
                        badge={<Badge type="coming soon" />}
                        dataAutomation="playbook-cadence-picker-personalization-ai-outreach"
                    />
                </Flex>
            </Stack>
        </Stack>
    );
};
